import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$ } from '../utils/formatters';
import { getRange } from '../utils/dateRange';
import { parseLocalDate } from '../utils/dates';
import Modal from './Modal';
import InvoiceDetailModal from './InvoiceDetailModal';
import InvoiceOriginalViewer from './InvoiceOriginalViewer';

const EXP_PRESETS=[{id:'this-month',label:'This Month'},{id:'last-3-months',label:'Last 3 Months'},{id:'ytd',label:'Year to Date'},{id:'all',label:'All Time'}];

export default function VendorInspect({vendor,orgId,t,ings,batches,mi,onClose,onEdit,onViewExpenses,setInvoiceModalId}){
const[row,setRow]=useState(null);
const[aliases,setAliases]=useState([]);
const[counts,setCounts]=useState(null);
const[loading,setLoading]=useState(true);
const[showExpenses,setShowExpenses]=useState(false);
const[expRows,setExpRows]=useState(null);
const[expPreset,setExpPreset]=useState('ytd');
const[showPurchases,setShowPurchases]=useState(false);
const[puRows,setPuRows]=useState(null);
const[puPreset,setPuPreset]=useState('ytd');
const[viewInvoiceId,setViewInvoiceId]=useState(null);
const[viewPrepaidPath,setViewPrepaidPath]=useState(null);
const[noInvoiceMsg,setNoInvoiceMsg]=useState(null);

useEffect(()=>{
  if(!vendor||!orgId)return;
  (async()=>{
    setLoading(true);
    const{data:vRow}=await supabase.from('vendors').select('*').eq('org_id',orgId).ilike('name',vendor).limit(1).maybeSingle();
    setRow(vRow);
    if(vRow){
      const{data:al}=await supabase.from('vendor_aliases').select('alias').eq('vendor_id',vRow.id);
      setAliases((al||[]).map(a=>a.alias));
    }
    const[inv,pu,ing,pp,ce]=await Promise.all([
      supabase.from('invoices').select('id',{count:'exact',head:true}).eq('org_id',orgId).ilike('vendor',vendor),
      supabase.from('purchases').select('id',{count:'exact',head:true}).eq('org_id',orgId).ilike('vendor',vendor).eq('is_superseded',false),
      supabase.from('ingredients').select('id',{count:'exact',head:true}).eq('org_id',orgId).ilike('distributor',vendor),
      supabase.from('prepaid_orders').select('id',{count:'exact',head:true}).eq('org_id',orgId).ilike('vendor',vendor),
      supabase.from('categorized_expenses').select('id',{count:'exact',head:true}).eq('org_id',orgId).ilike('vendor',vendor).eq('is_superseded',false),
    ]);
    setCounts({invoices:inv.count||0,purchases:pu.count||0,ingredients:ing.count||0,prepaid:pp.count||0,expenses:ce.count||0});
    setLoading(false);
  })();
},[vendor,orgId]);

useEffect(()=>{
  if(!showExpenses||!vendor||!orgId)return;
  if(expRows!==null)return; // already fetched
  (async()=>{
    const{data}=await supabase.from('categorized_expenses').select('*').eq('org_id',orgId).ilike('vendor',vendor).eq('is_superseded',false).order('date',{ascending:false});
    setExpRows(data||[]);
  })();
},[showExpenses,vendor,orgId]);

useEffect(()=>{
  if(!showPurchases||!vendor||!orgId)return;
  if(puRows!==null)return;
  (async()=>{
    const{data}=await supabase.from('purchases').select('*').eq('org_id',orgId).ilike('vendor',vendor).eq('is_superseded',false).order('date',{ascending:false});
    setPuRows(data||[]);
  })();
},[showPurchases,vendor,orgId]);

const filteredExp=(()=>{
  if(!expRows)return[];
  if(expPreset==='all')return expRows;
  const r=getRange(expPreset);
  return expRows.filter(e=>e.date>=r.start&&e.date<=r.end);
})();
const expTotal=filteredExp.reduce((s,e)=>s+Number(e.amount||0),0);

const filteredPu=(()=>{
  if(!puRows)return[];
  if(puPreset==='all')return puRows;
  const r=getRange(puPreset);
  return puRows.filter(p=>p.date>=r.start&&p.date<=r.end);
})();
const puTotal=filteredPu.reduce((s,p)=>s+Number(p.amount||0),0);

const mn="'DM Mono',monospace";
const sb={borderRadius:8,border:'none',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",fontWeight:500};
const sd={padding:'8px 10px',fontSize:12};
const sh={padding:'8px 10px',fontSize:9,fontWeight:600,color:t.td,textTransform:'uppercase',textAlign:'left'};
const lbl={fontSize:9,color:t.td,textTransform:'uppercase',marginBottom:2};
const val={fontSize:13,fontWeight:500};

return(<>
<Modal open={true} onClose={onClose} width={480} zIndex={35} t={t}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
<div style={{display:'flex',alignItems:'center',gap:10}}>
<div style={{fontSize:18,fontWeight:700}}>{vendor}</div>
{row&&<span style={{fontSize:10,padding:'2px 8px',borderRadius:4,background:row.vendor_category==='expense'?'rgba(212,168,36,0.1)':'rgba(59,130,246,0.1)',color:row.vendor_category==='expense'?t.yl:t.bl}}>{row.vendor_category||'inventory'}</span>}
</div>
<button onClick={onClose} style={{background:'none',border:'none',color:t.tm,fontSize:18,cursor:'pointer',padding:4}}>✕</button>
</div>

{loading?<div style={{textAlign:'center',padding:24,color:t.td}}>Loading...</div>:<>

{row?<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
<div><div style={lbl}>Status</div><div style={{...val,color:row.is_active?t.gn:t.td}}>{row.is_active?'Active':'Inactive'}</div></div>
<div><div style={lbl}>Default Cost Category</div><div style={val}>{row.default_expense_category||'—'}</div></div>
<div><div style={lbl}>Email Domain</div><div style={val}>{row.email_domain||'—'}</div></div>
<div><div style={lbl}>Contact</div><div style={val}>{row.contact_name||'—'}</div></div>
<div><div style={lbl}>Email</div><div style={val}>{row.contact_email||'—'}</div></div>
<div><div style={lbl}>Phone</div><div style={val}>{row.contact_phone||'—'}</div></div>
<div><div style={lbl}>Monitoring</div><div style={val}>{row.monitoring_mode==='force_on'?'Always On':row.monitoring_mode==='force_off'?'Off':'Auto'}</div></div>
</div>:<div style={{fontSize:12,color:t.td,marginBottom:16,fontStyle:'italic'}}>No vendor record found — name exists only as text in purchases.</div>}

{aliases.length>0&&<div style={{marginBottom:16}}>
<div style={{fontSize:11,fontWeight:600,marginBottom:4}}>Aliases</div>
<div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
{aliases.map(a=><span key={a} style={{fontSize:11,padding:'2px 8px',borderRadius:4,background:t.sf,border:`1px solid ${t.bd}`}}>{a}</span>)}
</div>
</div>}

{counts&&<div style={{background:t.sf,borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12}}>
{[['invoice',counts.invoices],['purchase',counts.purchases],['expense',counts.expenses],['ingredient',counts.ingredients],['prepaid order',counts.prepaid]].filter(([,c])=>c>0).map(([label,count],i)=>{const displayLabel=count===1?label:label+'s';const isClickable=(label==='expense'||label==='purchase')&&count>0;const handleClick=label==='expense'?()=>setShowExpenses(true):label==='purchase'?()=>setShowPurchases(true):null;return<span key={label}>{i>0?' · ':''}{isClickable?<span onClick={handleClick} style={{cursor:'pointer',color:t.bl,borderBottom:`1px dashed ${t.bl}`}}><span style={{fontWeight:600}}>{count}</span> {displayLabel}</span>:<><span style={{fontWeight:600}}>{count}</span> {displayLabel}</>}</span>;})}
{Object.values(counts).every(c=>c===0)&&<span style={{color:t.td,fontStyle:'italic'}}>No references found.</span>}
</div>}

</>}

<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',borderTop:`1px solid ${t.bd}`,paddingTop:12}}>
{onEdit?<button onClick={()=>{onEdit(vendor);onClose();}} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:12,fontWeight:500,padding:0,fontFamily:"'DM Sans',sans-serif"}}>Edit →</button>:<span/>}
<button onClick={onClose} style={{background:t.sf,border:`1px solid ${t.bd}`,borderRadius:6,padding:'6px 16px',fontSize:11,cursor:'pointer',color:t.tm,fontFamily:"'DM Sans',sans-serif"}}>Close</button>
</div>
</Modal>

{showExpenses&&<Modal open={true} onClose={()=>setShowExpenses(false)} width={620} zIndex={36} t={t}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<div style={{fontSize:16,fontWeight:700}}>{vendor} — Expenses</div>
<button onClick={()=>setShowExpenses(false)} style={{background:'none',border:'none',color:t.tm,fontSize:18,cursor:'pointer',padding:4}}>✕</button>
</div>

<div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
{EXP_PRESETS.map(p=><button key={p.id} onClick={()=>setExpPreset(p.id)} style={{...sb,background:expPreset===p.id?t.bl:t.sf,color:expPreset===p.id?'#fff':t.tm,border:`1px solid ${expPreset===p.id?t.bl:t.bd}`,padding:'5px 12px',fontSize:10}}>{p.label}</button>)}
</div>

{expRows===null?<div style={{textAlign:'center',padding:24,color:t.td}}>Loading...</div>:<>

<div style={{background:t.sf,borderRadius:8,padding:'10px 14px',marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
<span style={{fontSize:12,color:t.tm}}>{filteredExp.length} expense{filteredExp.length!==1?'s':''}</span>
<span style={{fontFamily:mn,fontWeight:600,fontSize:14,color:expTotal<0?t.bl:t.ac}}>{expTotal<0?'−$'+fmt$(Math.abs(expTotal)):'$'+fmt$(expTotal)}</span>
</div>

{filteredExp.length===0?<div style={{textAlign:'center',padding:24,color:t.td,fontSize:12}}>No expenses in this period.</div>:
<div style={{border:`1px solid ${t.bd}`,borderRadius:8,overflow:'hidden',maxHeight:'50vh',overflowY:'auto'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>{['Date','Description','Category','Amount',''].map(h=><th key={h} style={sh}>{h}</th>)}</tr></thead>
<tbody>{filteredExp.map((e,i)=><tr key={e.id||i} style={{borderBottom:`1px solid ${t.bd}`,cursor:e.source_invoice_id?'pointer':'default'}} onClick={()=>{if(e.source_invoice_id){setViewInvoiceId(e.source_invoice_id);setNoInvoiceMsg(null);}else{setNoInvoiceMsg(e.id);setTimeout(()=>setNoInvoiceMsg(p=>p===e.id?null:p),3000);}}} onMouseOver={ev=>{if(e.source_invoice_id)ev.currentTarget.style.background=t.sfh;}} onMouseOut={ev=>ev.currentTarget.style.background='transparent'}>
<td style={{...sd,fontSize:11,color:t.tm}}>{parseLocalDate(e.date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'})}</td>
<td style={{...sd,fontWeight:500,fontSize:11}}>{e.description||'—'}{noInvoiceMsg===e.id&&<div style={{fontSize:9,color:t.td,marginTop:2}}>Manual entry — no invoice</div>}</td>
<td style={sd}><span style={{fontSize:9,padding:'2px 5px',borderRadius:3,background:'rgba(139,143,158,0.12)',color:t.tm}}>{e.category||'Miscellaneous'}</span></td>
<td style={{...sd,fontFamily:mn,fontWeight:600,fontSize:11,color:Number(e.amount)<0?t.bl:t.ac}}>{Number(e.amount)<0?'−$'+fmt$(Math.abs(e.amount)):'$'+fmt$(e.amount)}</td>
<td style={{...sd,width:20}}>{e.source_invoice_id&&<span style={{fontSize:9,color:t.bl}} title="From invoice">📄</span>}</td>
</tr>)}</tbody>
</table>
</div>}

</>}
</Modal>}

{showPurchases&&<Modal open={true} onClose={()=>setShowPurchases(false)} width={620} zIndex={36} t={t}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<div style={{fontSize:16,fontWeight:700}}>{vendor} — Purchases</div>
<button onClick={()=>setShowPurchases(false)} style={{background:'none',border:'none',color:t.tm,fontSize:18,cursor:'pointer',padding:4}}>✕</button>
</div>

<div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
{EXP_PRESETS.map(p=><button key={p.id} onClick={()=>setPuPreset(p.id)} style={{...sb,background:puPreset===p.id?t.bl:t.sf,color:puPreset===p.id?'#fff':t.tm,border:`1px solid ${puPreset===p.id?t.bl:t.bd}`,padding:'5px 12px',fontSize:10}}>{p.label}</button>)}
</div>

{puRows===null?<div style={{textAlign:'center',padding:24,color:t.td}}>Loading...</div>:<>

<div style={{background:t.sf,borderRadius:8,padding:'10px 14px',marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
<span style={{fontSize:12,color:t.tm}}>{filteredPu.length} purchase{filteredPu.length!==1?'s':''}</span>
<span style={{fontFamily:mn,fontWeight:600,fontSize:14,color:puTotal<0?t.bl:t.yl}}>{puTotal<0?'−$'+fmt$(Math.abs(puTotal)):'$'+fmt$(puTotal)}</span>
</div>

{filteredPu.length===0?<div style={{textAlign:'center',padding:24,color:t.td,fontSize:12}}>No purchases in this period.</div>:
<div style={{border:`1px solid ${t.bd}`,borderRadius:8,overflow:'hidden',maxHeight:'50vh',overflowY:'auto'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>{['Date','Category','Amount',''].map(h=><th key={h} style={sh}>{h}</th>)}</tr></thead>
<tbody>{filteredPu.map((p,i)=>{const hasLink=!!(p.invoice_id||p.prepaid_order_id);return<tr key={p.id||i} style={{borderBottom:`1px solid ${t.bd}`,cursor:hasLink?'pointer':'default'}} onClick={async()=>{if(p.invoice_id){setViewInvoiceId(p.invoice_id);setNoInvoiceMsg(null);}else if(p.prepaid_order_id){setNoInvoiceMsg(null);const{data:po}=await supabase.from('prepaid_orders').select('file_url').eq('id',p.prepaid_order_id).single();if(po?.file_url)setViewPrepaidPath(po.file_url);else{setNoInvoiceMsg(p.id);setTimeout(()=>setNoInvoiceMsg(pr=>pr===p.id?null:pr),3000);}}else{setNoInvoiceMsg(p.id);setTimeout(()=>setNoInvoiceMsg(pr=>pr===p.id?null:pr),3000);}}} onMouseOver={e=>{if(hasLink)e.currentTarget.style.background=t.sfh;}} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<td style={{...sd,fontSize:11,color:t.tm}}>{parseLocalDate(p.date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'})}{noInvoiceMsg===p.id&&<div style={{fontSize:9,color:t.td,marginTop:2}}>No invoice attached</div>}</td>
<td style={sd}><span style={{fontSize:9,padding:'2px 5px',borderRadius:3,background:'rgba(59,130,246,0.1)',color:t.bl}}>{p.category||'Food'}</span></td>
<td style={{...sd,fontFamily:mn,fontWeight:600,fontSize:11,color:Number(p.amount)<0?t.bl:t.yl}}>{Number(p.amount)<0?'−$'+fmt$(Math.abs(p.amount)):'$'+fmt$(p.amount)}</td>
<td style={{...sd,width:20}}>{(p.invoice_id||p.prepaid_order_id)&&<span style={{fontSize:9,color:t.bl}} title="From invoice">📄</span>}</td>
</tr>;})}</tbody>
</table>
</div>}

</>}
</Modal>}

{viewInvoiceId&&<InvoiceDetailModal invoiceId={viewInvoiceId} onClose={()=>setViewInvoiceId(null)} ings={ings||[]} batches={batches||[]} mi={mi||[]} orgId={orgId} t={t} sb={sb} sh={sh} sd={sd} mn={mn} zIndex={41} nested={true}/>}
{viewPrepaidPath&&<Modal open={true} onClose={()=>setViewPrepaidPath(null)} width={700} zIndex={37} t={t}><InvoiceOriginalViewer storagePath={viewPrepaidPath} t={t} defaultOpen={true}/></Modal>}
</>);
}
